import React, { useState, useEffect, useCallback } from 'react';
import { Card, DatePicker, Button, Table, Statistic, Row, Col, Progress, message, Alert } from 'antd';
import dayjs from 'dayjs';
import { getAttendanceReport } from '../../api/reports';
import usePersistedPageSize from '../../hooks/usePersistedPageSize';

const { RangePicker } = DatePicker;

const AttendanceReportTab = () => {
  const [range, setRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('report-attendance', 20);
  // Liste sunucu tarafinda sayfali, bu yuzden siralama da SUNUCUDA yapiliyor.
  const [sorter, setSorter] = useState({ field: null, order: null });

  const fetch = useCallback(async (page = 1, perPage = persistedPageSize, sort = sorter) => {
    if (!range) return;
    setLoading(true);
    try {
      const res = await getAttendanceReport({
        start_date: range[0].format('YYYY-MM-DD'),
        end_date:   range[1].format('YYYY-MM-DD'),
        page,
        per_page: perPage,
        sort_by: sort?.field || undefined,
        sort_direction: sort?.order === 'ascend' ? 'asc' : (sort?.order === 'descend' ? 'desc' : undefined),
      });
      setData(res.data);
    } catch {
      message.error('Devam raporu yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetch(1, persistedPageSize); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // sorter: true = siralamayi sunucu yapiyor. Kolon adlari backend'deki beyaz
  // listeyle birebir ayni olmali.
  const sortProps = (field) => ({
    sorter: true,
    sortOrder: sorter.field === field ? sorter.order : null,
  });

  const columns = [
    {
      title: 'Öğrenci',
      dataIndex: 'student_name',
      ...sortProps('student_name'),
    },
    {
      title: 'Şube',
      dataIndex: 'branch',
      ...sortProps('branch'),
      render: v => v || '—',
    },
    {
      title: 'Geldi',
      dataIndex: 'present',
      align: 'center',
      ...sortProps('present'),
      render: v => <span className="text-green-600 font-medium">{v}</span>,
    },
    {
      title: 'Gelmedi',
      dataIndex: 'absent',
      align: 'center',
      ...sortProps('absent'),
      render: v => <span className="text-red-500 font-medium">{v}</span>,
    },
    {
      title: 'Planlı',
      dataIndex: 'planned_absent',
      align: 'center',
      ...sortProps('planned_absent'),
      render: v => <span className="text-orange-500 font-medium">{v || 0}</span>,
    },
    { title: 'Toplam', dataIndex: 'total', align: 'center', ...sortProps('total') },
    {
      title: 'Devam Oranı',
      dataIndex: 'attendance_rate',
      align: 'center',
      ...sortProps('attendance_rate'),
      render: v => (
        <div className="flex items-center gap-2">
          <Progress
            percent={v}
            size="small"
            style={{ width: 80, marginBottom: 0 }}
            strokeColor={v >= 80 ? '#52c41a' : v >= 60 ? '#faad14' : '#ff4d4f'}
          />
          <span className="text-xs">{v}%</span>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <RangePicker value={range} onChange={setRange} format="DD.MM.YYYY" />
        <Button type="primary" onClick={() => fetch(1, persistedPageSize)} loading={loading}>Getir</Button>
      </div>

      {data && (
        <>
          <Row gutter={16} className="mb-4">
            <Col span={6}>
              <Card size="small">
                <Statistic title="Toplam Katılım" value={data.summary.present} valueStyle={{ color: '#52c41a' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="Devamsızlık" value={data.summary.absent} valueStyle={{ color: '#ff4d4f' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="Mazeretli Devamsız" value={data.summary.planned_absent ?? 0} valueStyle={{ color: '#fa8c16' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="Genel Devam Oranı"
                  value={data.summary.attendance_rate}
                  suffix="%"
                  valueStyle={{ color: data.summary.attendance_rate >= 80 ? '#52c41a' : '#faad14' }}
                />
              </Card>
            </Col>
          </Row>

          {data.low_attendance_count > 0 && (
            <Alert
              className="mb-4"
              type="warning"
              showIcon
              message={`${data.low_attendance_count} öğrencinin devam oranı %70'in altında`}
            />
          )}

          <Table
            dataSource={data.per_student}
            rowKey="student_id"
            columns={columns}
            size="small"
            loading={loading}
            // Sayfalama ve siralama tek elden: pagination.onChange ayrica
            // taniml kalsaydi sayfa degisiminde iki istek giderdi.
            onChange={(pag, _filters, nextSorter) => {
              const next = nextSorter?.order
                ? { field: nextSorter.field, order: nextSorter.order }
                : { field: null, order: null };
              const sortDegisti = next.field !== sorter.field || next.order !== sorter.order;
              setSorter(next);
              setPersistedPageSize(pag.pageSize);
              fetch(sortDegisti ? 1 : pag.current, pag.pageSize, next);
            }}
            pagination={{
              current: data.current_page,
              pageSize: data.per_page,
              total: data.total,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100'],
              showTotal: (total) => `Toplam ${total} öğrenci`,
            }}
            rowClassName={r => r.attendance_rate < 70 ? 'bg-red-50' : ''}
          />
        </>
      )}
    </div>
  );
};

export default AttendanceReportTab;
